// src/Pages/ApplyReservation.js
import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { doc, getDoc, collection, addDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import emailjs from "emailjs-com";
import LoadingAnimation from "../components/LoadingAnimation";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// ─── HTML ENTITY DECODER ────────────────────────────────────────────────────
function decodeHtml(str) {
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
}

// ─── ADMIN EMAIL BUILDER ────────────────────────────────────────────────────
function buildAdminEmail(data, opportunityTitle, selectedField, resumeURL, emailLogo) {
  const year = new Date().getFullYear();
  const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";

  const rows = [
    ["&#128100;", "Full Name", data.name],
    ["&#128231;", "Email", data.email],
    ["&#128196;", "CNIC", data.cnic],
    ["&#128222;", "Phone", data.phone],
    ["&#128205;", "Location", data.location],
    ["&#128197;", "Interview Date", data.interviewDate],
    ["&#128336;", "Interview Slot", data.interviewSlot],
    ["&#128188;", "Opportunity Type", opportunityTitle],
    ["&#128188;", "Selected Field", selectedField],
  ];

  const tableRows = rows
    .map(function (row) {
      return (
        "<tr>" +
        "<td style='padding:12px 18px;width:40%;border-bottom:1px solid #f5f5f5;'>" +
        "<span style='font-size:16px;margin-right:8px;'>" +
        row[0] +
        "</span>" +
        "<span style='font-weight:600;color:#444;font-size:13px;text-transform:uppercase;letter-spacing:0.5px;'>" +
        row[1] +
        "</span>" +
        "</td>" +
        "<td style='padding:12px 18px;color:#111;font-size:14px;font-weight:500;border-bottom:1px solid #f5f5f5;'>" +
        row[2] +
        "</td>" +
        "</tr>"
      );
    })
    .join("");

  return (
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
    "<tr><td align='center'>" +
    "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +
    // HEADER
    "<tr>" +
    "<td style='background:#ff8000;text-align:center;padding:36px 24px 28px;'>" +
    "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
    "<h1 style='margin:0;color:#ffffff;font-size:22px;font-weight:700;letter-spacing:0.3px;'>New Opportunity Application Received</h1>" +
    "<p style='margin:8px 0 0;color:rgba(255,255,255,0.82);font-size:13px;'>Submitted via VisionBird Careers Portal</p>" +
    "</td>" +
    "</tr>" +
    // ALERT BANNER
    "<tr>" +
    "<td style='padding:24px 28px 0;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fff8f0;border-radius:10px;border:1px solid #ffd199;'>" +
    "<tr>" +
    "<td style='padding:4px 0 4px 14px;width:4px;background:#ff8000;border-radius:10px 0 0 10px;'></td>" +
    "<td style='padding:14px 16px;'>" +
    "<p style='margin:0;color:#b35900;font-size:13px;font-weight:600;'>&#128276; Action Required</p>" +
    "<p style='margin:4px 0 0;color:#804000;font-size:13px;'>Review this application and follow up if the candidate matches your requirements.</p>" +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +
    // APPLICANT DETAILS
    "<tr>" +
    "<td style='padding:24px 28px 8px;'>" +
    "<p style='margin:0 0 12px;font-size:11px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:1.2px;'>Applicant Details</p>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='border:1px solid #eeeeee;border-radius:10px;overflow:hidden;'>" +
    tableRows +
    "</table>" +
    "</td>" +
    "</tr>" +
    // RESUME BUTTON
    "<tr>" +
    "<td style='padding:20px 28px 32px;text-align:center;'>" +
    "<a href='" +
    resumeURL +
    "' target='_blank' rel='noopener noreferrer' style='display:inline-block;background:#ff8000;color:#ffffff;text-decoration:none;padding:14px 40px;border-radius:50px;font-weight:700;font-size:14px;letter-spacing:0.3px;'>View Resume</a>" +
    "</td>" +
    "</tr>" +
    // FOOTER
    "<tr>" +
    "<td style='background:#111111;text-align:center;padding:22px;'>" +
    "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
    "<p style='margin:0;color:#666;font-size:12px;'>&#169; " +
    year +
    " &nbsp;&#183;&nbsp; Automated Notification &nbsp;&#183;&nbsp; Do not reply</p>" +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td></tr>" +
    "</table>"
  );
}

// ─── CANDIDATE EMAIL BUILDER ────────────────────────────────────────────────
function buildCandidateEmail(data, opportunityTitle, selectedField, emailLogo) {
  const year = new Date().getFullYear();
  const logoUrl = emailLogo || "https://i.postimg.cc/023xvzzm/vbt-logo-2.png";

  const progressSteps = [
    { icon: "&#10003;", label: "Application<br>Received", active: true },
    { icon: "&#128269;", label: "Profile<br>Under Review", active: false },
    { icon: "&#127897;", label: "Interview<br>Scheduled", active: false },
  ];

  const progressCells = progressSteps
    .map(function (step, i) {
      const circle =
        "<div style='width:48px;height:48px;border-radius:50%;margin:0 auto 10px;" +
        "background:" +
        (step.active ? "#ff8000" : "#ebebeb") +
        ";" +
        "line-height:48px;font-size:20px;text-align:center;color:" +
        (step.active ? "#fff" : "#aaa") +
        ";'>" +
        step.icon +
        "</div>" +
        "<p style='margin:0;font-size:11px;font-weight:700;color:" +
        (step.active ? "#ff6600" : "#aaa") +
        ";text-align:center;line-height:1.5;'>" +
        step.label +
        "</p>";

      const connector =
        "<td style='padding-bottom:32px;'>" +
        "<div style='height:2px;background:" +
        (step.active ? "#ffc680" : "#e8e8e8") +
        ";margin-top:24px;'></div>" +
        "</td>";

      const cell =
        "<td style='text-align:center;padding:0 4px;'>" + circle + "</td>";
      return i < progressSteps.length - 1 ? cell + connector : cell;
    })
    .join("");

  const checklistItems = [
    {
      icon: "&#128336;",
      color: "#ff8000",
      bg: "#fff8f0",
      border: "#ffd199",
      title: "Be On Time",
      desc: "Please arrive at least <strong>10 minutes early</strong>. Late arrivals may not be accommodated.",
    },
    {
      icon: "&#128196;",
      color: "#0055cc",
      bg: "#f0f6ff",
      border: "#b3d0ff",
      title: "Bring Your ID Card",
      desc: "Carry your original <strong>CNIC or any valid government-issued ID</strong> for verification.",
    },
    {
      icon: "&#128187;",
      color: "#1a7a45",
      bg: "#f0fff6",
      border: "#99e8c0",
      title: "Bring Your Laptop",
      desc: "Bring a <strong>fully charged laptop</strong>. The assessment or practical task may take up to 1 hour, so please ensure your device is ready to use.",
    },
  ];

  const checklistRows = checklistItems
    .map(function (item) {
      return (
        "<tr>" +
        "<td style='padding:6px 0;'>" +
        "<table width='100%' cellpadding='0' cellspacing='0' style='background:" +
        item.bg +
        ";border:1px solid " +
        item.border +
        ";border-radius:10px;margin-bottom:10px;'>" +
        "<tr>" +
        "<td style='padding:14px 16px;width:48px;text-align:center;font-size:24px;vertical-align:middle;'>" +
        item.icon +
        "</td>" +
        "<td style='padding:14px 16px 14px 0;vertical-align:middle;'>" +
        "<p style='margin:0 0 3px;font-weight:700;color:" +
        item.color +
        ";font-size:13px;'>" +
        item.title +
        "</p>" +
        "<p style='margin:0;color:#555;font-size:13px;line-height:1.6;'>" +
        item.desc +
        "</p>" +
        "</td>" +
        "</tr>" +
        "</table>" +
        "</td>" +
        "</tr>"
      );
    })
    .join("");

  return (
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#f0f4f8;padding:36px 0;font-family:Segoe UI,Arial,sans-serif;'>" +
    "<tr><td align='center'>" +
    "<table width='600' cellpadding='0' cellspacing='0' style='background:#ffffff;border-radius:16px;overflow:hidden;'>" +
    // HEADER
    "<tr>" +
    "<td style='background:#ff8000;text-align:center;padding:36px 24px 30px;'>" +
    "<img src='" + logoUrl + "' width='160px' alt='VBT' style='display:block;margin:0 auto 18px;' />" +
    "<h1 style='margin:0;color:#ffffff;font-size:24px;font-weight:700;'>Application Received!</h1>" +
    "<p style='margin:8px 0 0;color:rgba(255,255,255,0.85);font-size:14px;'>You are one step closer to joining VisionBird Technologies</p>" +
    "</td>" +
    "</tr>" +
    // GREETING
    "<tr>" +
    "<td style='padding:32px 32px 16px;'>" +
    "<p style='margin:0;font-size:16px;color:#222;line-height:1.7;'>Hi <strong style='color:#ff6600;'>" +
    data.name +
    "</strong>,</p>" +
    "<p style='margin:10px 0 0;font-size:14px;color:#555;line-height:1.8;'>" +
    "Thank you for applying for <strong style='color:#111;'>" +
    opportunityTitle +
    "</strong> as a <strong style='color:#111;'>" +
    selectedField +
    "</strong> at " +
    "<strong style='color:#ff6600;'>VisionBird Technologies</strong>. " +
    "We have successfully received your application and our team will carefully review your profile." +
    "</p>" +
    "</td>" +
    "</tr>" +
    // INTERVIEW DETAILS
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fff8f0;border:1px solid #ffd199;border-radius:12px;overflow:hidden;'>" +
    "<tr>" +
    "<td style='background:#ff8000;padding:12px 18px;'>" +
    "<p style='margin:0;color:#fff;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;'>&#128197; &nbsp;Your Interview Details</p>" +
    "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:16px 18px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0'>" +
    "<tr>" +
    "<td style='padding:7px 0;color:#888;font-size:13px;width:40%;'>Opportunity Type</td>" +
    "<td style='padding:7px 0;color:#111;font-size:13px;font-weight:600;'>" +
    opportunityTitle +
    "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:7px 0;color:#888;font-size:13px;border-top:1px solid #f5e6d0;'>Selected Field</td>" +
    "<td style='padding:7px 0;color:#111;font-size:13px;font-weight:600;border-top:1px solid #f5e6d0;'>" +
    selectedField +
    "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:7px 0;color:#888;font-size:13px;border-top:1px solid #f5e6d0;'>Interview Date</td>" +
    "<td style='padding:7px 0;color:#111;font-size:13px;font-weight:600;border-top:1px solid #f5e6d0;'>" +
    data.interviewDate +
    "</td>" +
    "</tr>" +
    "<tr>" +
    "<td style='padding:7px 0;color:#888;font-size:13px;border-top:1px solid #f5e6d0;'>Time Slot</td>" +
    "<td style='padding:7px 0;color:#111;font-size:13px;font-weight:600;border-top:1px solid #f5e6d0;'>" +
    data.interviewSlot +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +
    // PROGRESS STEPS
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<table width='100%' cellpadding='0' cellspacing='0' style='background:#fafafa;border:1px solid #eeeeee;border-radius:12px;padding:20px 16px;'>" +
    "<tr><td>" +
    "<p style='margin:0 0 18px;text-align:center;font-size:11px;font-weight:700;color:#bbb;text-transform:uppercase;letter-spacing:1.2px;'>Application Progress</p>" +
    "<table width='100%' cellpadding='0' cellspacing='0'><tr>" +
    progressCells +
    "<tr></table>" +
    "</td></tr>" +
    "</table>" +
    "</td>" +
    "</tr>" +
    // INTERVIEW CHECKLIST
    "<tr>" +
    "<td style='padding:8px 32px 16px;'>" +
    "<p style='margin:0 0 12px;font-size:11px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:1.2px;'>Before You Arrive</p>" +
    "<table width='100%' cellpadding='0' cellspacing='0'>" +
    checklistRows +
    "</table>" +
    "</td>" +
    "</tr>" +
    // CLOSING
    "<tr>" +
    "<td style='padding:8px 32px 28px;border-top:1px solid #f5f5f5;margin-top:8px;'>" +
    "<p style='margin:0;font-size:14px;color:#555;line-height:1.8;'>We look forward to meeting you. Best of luck! &#127919;</p>" +
    "<p style='margin:14px 0 0;font-size:14px;color:#333;'>Warm regards,<br><strong style='color:#ff6600;'>VisionBird Technologies &mdash; HR Team</strong></p>" +
    "</td>" +
    "</tr>" +
    // FOOTER
    "<tr>" +
    "<td style='background:#111111;text-align:center;padding:22px;'>" +
    "<p style='margin:0 0 4px;color:#ff8000;font-size:13px;font-weight:700;letter-spacing:0.5px;'>VISIONBIRD TECHNOLOGIES</p>" +
    "<p style='margin:0;color:#666;font-size:12px;'>&#169; " +
    year +
    " &nbsp;&#183;&nbsp; Please do not reply to this email</p>" +
    "</td>" +
    "</tr>" +
    "</table>" +
    "</td></tr>" +
    "</table>"
  );
}

const ApplyReservation = () => {
  const { id } = useParams();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [genderError, setGenderError] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [emailLogo, setEmailLogo] = useState("");
  const [cnicBlockedModal, setCnicBlockedModal] = useState({ visible: false, daysLeft: 0 });
  const [holidayDates, setHolidayDates] = useState([]);
  const errorRef = useRef(null);

  const availableFields = [
    "Graphic Designer",
    "UI & UX Designer",
    "Motion Graphic Designer",
    "Front End Developer",
    "Backend Developer",
    "Full Stack Developer"
  ];

  useEffect(() => {
    const fetchOpportunity = async () => {
      try {
        const docRef = doc(db, "reservations", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setOpportunity({ ...data, type: (data.type || "").replace(/&#x2F;/gi, "/") });
        } else {
          console.log("❌ Opportunity not found!");
          toast.error("Opportunity not found!");
        }

        const logosRef = doc(db, "logos", "xrSCDlcVzp10becG8bnW");
        const logosSnap = await getDoc(logosRef);
        if (logosSnap.exists()) {
          const logoData = logosSnap.data();
          setEmailLogo(logoData.emailLogo || "");
        }

      } catch (error) {
        console.error("❌ Error fetching opportunity:", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchOpportunity();
  }, [id]);

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const snapshot = await getDocs(collection(db, "slots"));
        const list = snapshot.docs.map((doc) => {
          const data = doc.data();
          return { label: data.slot, time: data.time };
        });
        setAvailableSlots(list);
      } catch (error) {
        console.error("Error fetching slots:", error);
        toast.error("Failed to load time slots. Please try again.");
      }
    };
    fetchSlots();
  }, []);

  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  // ── Load public holidays at mount (so the check is synchronous on submit) ──
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const ref = doc(db, "settings", "publicHolidays");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setHolidayDates(snap.data().dates || []);
        }
      } catch (err) {
        // Rules may block unauthenticated reads — log for visibility.
        console.warn("[PublicHolidays] Could not load holiday dates:", err.code || err.message);
      }
    };
    fetchHolidays();
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    cnic: "",
    phone: "",
    email: "",
    location: "",
    selectedField: "",
    interviewDate: "",
    interviewSlot: "",
    candidateGender: "",
    resumeOption: "file",
    resumeFile: null,
    resumeLink: "",
  });

  const [fileName, setFileName] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, resumeFile: file });
    setFileName(file ? file.name : "");
  };

  const uploadResumeToCloudinary = async (file) => {
    const url = `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/raw/upload`;
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    const res = await fetch(url, { method: "POST", body: data });
    const json = await res.json();
    return json.secure_url;
  };
  const handleDateChange = async (e) => {
    const selectedDateInput = e.target.value;

    if (isPublicHoliday(selectedDateInput)) {
      showHolidayToast(selectedDateInput);
      setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      return;
    }

    const selected = new Date(selectedDateInput);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selected < today) {
      toast.error("You cannot select a past date. Please choose today or a future date.");
      setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      return;
    }

    const day = selected.getDay();
    if (day === 0 || day === 6) {
      toast.error("Weekends (Saturday and Sunday) are not available for interviews.");
      setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      return;
    }

    const formattedDate = selected.toLocaleDateString("en-GB").replace(/\//g, "-");
    setFormData({ ...formData, interviewDate: selectedDateInput, interviewSlot: "" });

    try {
      const applicationsQuery = query(
        collection(db, "opportunityApplications"),
        where("interviewDate", "==", formattedDate)
      );
      const applicationsSnap = await getDocs(applicationsQuery);
      const bookedFromApplications = applicationsSnap.docs.map(
        (doc) => doc.data().interviewSlot
      );

      const allBooked = [...bookedFromApplications];
      setBookedSlots(allBooked);

      if (allBooked.length >= availableSlots.length) {
        toast.error("All interview slots for this date are already booked.");
        setFormData({ ...formData, interviewDate: "", interviewSlot: "" });
      }
    } catch (error) {
      console.error("Error checking slots:", error);
      toast.error("Error checking available slots. Try again.");
    }
  };

  const handleGenderChange = (e) => {
    const selectedGender = e.target.value;
    if (
      (opportunity?.gender === "Male" && selectedGender === "Female") ||
      (opportunity?.gender === "Female" && selectedGender === "Male")
    ) {
      setGenderError(true);
      setTimeout(() => {
        errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      setFormData({
        name: "",
        cnic: "",
        phone: "",
        email: "",
        location: "",
        selectedField: "",
        interviewDate: "",
        interviewSlot: "",
        resumeOption: "file",
        resumeFile: null,
        resumeLink: "",
        candidateGender: "",
      });
      setFileName("");
    } else {
      setGenderError(false);
      setFormData({ ...formData, candidateGender: selectedGender });
    }
  };

  const filteredSlots = availableSlots
    .filter((slot) => !bookedSlots.includes(slot.time))
    .sort((a, b) => {
      const numA = parseInt(a.label.replace("Slot ", ""));
      const numB = parseInt(b.label.replace("Slot ", ""));
      return numA - numB;
    });

  // ── CNIC 30-DAY RESTRICTION ─────────────────────────────────────────────
  // Returns { blocked: true, daysLeft } if the CNIC was rejected/blocked
  // within the last 30 days; otherwise returns { blocked: false }.
  const checkCnicRestriction = async (cnic) => {
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    try {
      const q = query(
        collection(db, "opportunityApplications"),
        where("cnic", "==", cnic)
      );
      const snap = await getDocs(q);

      for (const docSnap of snap.docs) {
        const data = docSnap.data();
        const rawStatus = (data.status || "").toLowerCase();
        const isRestricted = rawStatus === "rejected" || rawStatus === "blocked" || rawStatus === "pending" || rawStatus === "";

        if (isRestricted) {
          // Prefer statusUpdatedAt, fall back to createdAt
          const tsField = data.statusUpdatedAt || data.createdAt;
          const ts = tsField?.toDate?.()?.getTime?.() || 0;
          const elapsed = now - ts;

          if (elapsed < THIRTY_DAYS) {
            const daysLeft = Math.ceil((THIRTY_DAYS - elapsed) / (24 * 60 * 60 * 1000));
            return { blocked: true, daysLeft };
          }
        }
      }
    } catch (err) {
      console.error("CNIC restriction check failed:", err);
      // On error we allow submission to proceed (fail-open)
    }
    return { blocked: false, daysLeft: 0 };
  };

  const sendEmailNotification = async (formData, opportunityTitle, selectedField, resumeURL) => {
    const cleanTitle = (opportunityTitle || "").replace(/&#x2F;/gi, "/");

    try {
      // ADMIN — candidate reply is sent manually from the admin panel after review
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          to_email: "jobs@visionbird.com",
          subject: "New Opportunity Application — " + cleanTitle + " (" + formData.name + ")",
          message: buildAdminEmail(formData, opportunityTitle, selectedField, resumeURL, emailLogo),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Error sending email notification.");
    }
  };

  // ── PUBLIC HOLIDAY CHECK (synchronous — state loaded at mount) ───────────
  // Uses en-CA locale which gives YYYY-MM-DD in *local* timezone,
  // matching exactly what the admin date-picker stores.
  const isPublicHoliday = (dateStr) => {
    const targetDate = dateStr || new Date().toLocaleDateString("en-CA"); // "YYYY-MM-DD" local
    return holidayDates.some(h => (typeof h === "object" ? h.date === targetDate : h === targetDate));
  };

  const getHolidayNote = (dateStr) => {
    const targetDate = dateStr || new Date().toLocaleDateString("en-CA");
    const holiday = holidayDates.find(h => (typeof h === "object" ? h.date === targetDate : h === targetDate));
    return (holiday && typeof holiday === "object") ? holiday.note : "";
  };

  // ── PROFESSIONAL HOLIDAY TOAST ────────────────────────────────────────────
  const showHolidayToast = (dateStr) => {
    const note = getHolidayNote(dateStr);
    let dateObj;
    if (dateStr) {
      const [year, month, day] = dateStr.split("-");
      dateObj = new Date(Number(year), Number(month) - 1, Number(day));
    } else {
      dateObj = new Date();
    }
    const dateLabel = dateObj.toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    toast(
      <div style={{ display: "flex", gap: "14px", alignItems: "flex-start", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ background: "#FFEDD5", color: "#EA580C", padding: "10px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <i className="fas fa-calendar-times" style={{ fontSize: "20px" }}></i>
        </div>
        <div style={{ lineHeight: 1.5 }}>
          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 15, color: "#1F2937" }}>
            Date Unavailable
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "#4B5563", paddingRight: "4px" }}>
            We apologize, but our team will be away on <strong style={{ color: "#111827" }}>{dateLabel}</strong>
            {note ? ` currently because of ${note}` : ` due to a holiday`}.
            Please select another date.
          </p>
        </div>
      </div>,
      {
        toastId: "holiday-toast",
        position: "bottom-left",
        autoClose: 7000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        icon: false,
        style: {
          background: "#ffffff",
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          padding: "16px",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          width: "380px",
        },
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // ── Public holiday gate (synchronous) ────────────────────────────────
    if (formData.interviewDate && isPublicHoliday(formData.interviewDate)) {
      showHolidayToast(formData.interviewDate);
      setSubmitting(false);
      return;
    }

    const { name, cnic, phone, email, location, selectedField, interviewDate, interviewSlot, resumeOption, resumeFile, resumeLink, candidateGender } = formData;

    if (name.trim().length < 4) {
      toast.error("Name must be at least 4 characters long.");
      setSubmitting(false);
      return;
    }
    if (!/^\d{13}$/.test(cnic)) {
      toast.error("Please enter a valid 13-digit CNIC number (digits only).");
      setSubmitting(false);
      return;
    }
    if (!/^\d+$/.test(phone)) {
      toast.error("Phone number must contain only numbers.");
      setSubmitting(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      setSubmitting(false);
      return;
    }
    if (!location || !selectedField || !interviewDate || !interviewSlot) {
      toast.error("Please fill in all required fields before submitting.");
      setSubmitting(false);
      return;
    }

    // ── CNIC 30-day restriction check ──────────────────────────────────
    const restriction = await checkCnicRestriction(cnic);
    if (restriction.blocked) {
      setCnicBlockedModal({ visible: true, daysLeft: restriction.daysLeft });
      setSubmitting(false);
      return;
    }

    try {
      let resumeURL = "";
      if (resumeOption === "file" && resumeFile) {
        resumeURL = await uploadResumeToCloudinary(resumeFile);
      } else if (resumeOption === "link" && resumeLink) {
        resumeURL = resumeLink;
      }

      const formattedDate = new Date(interviewDate).toLocaleDateString("en-GB").replace(/\//g, "-");

      await addDoc(collection(db, "opportunityApplications"), {
        name,
        cnic,
        phone,
        email,
        location,
        selectedField,
        candidateGender,
        interviewDate: formattedDate,
        interviewSlot,
        resumeURL,
        opportunityTitle: opportunity?.type || "N/A",
        opportunityId: id,
        createdAt: serverTimestamp(),
        // New status fields — all new applications start as pending
        status: "pending",
        statusUpdatedAt: serverTimestamp(),
      });

      await sendEmailNotification(formData, opportunity?.type || "N/A", selectedField, resumeURL);

      toast.success("Application submitted successfully!");
      setFormData({
        name: "",
        cnic: "",
        phone: "",
        email: "",
        location: "",
        selectedField: "",
        interviewDate: "",
        interviewSlot: "",
        candidateGender: "",
        resumeOption: "file",
        resumeFile: null,
        resumeLink: "",
      });
      setFileName("");
      setBookedSlots([]);
      setSubmitting(false);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Submission failed: " + error.message);
      setSubmitting(false);
    }
  };

  const today = new Date();
  const firstDay = today.toISOString().split("T")[0];

  if (loading) return <>
    <Navbar />
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <LoadingAnimation text="Loading Job Description..." />
    </div>
  </>;
  if (!opportunity) return <p style={{ textAlign: "center", padding: "50px" }}>Opportunity not found.</p>;

  return (
    <>
      <Helmet>
        <title>Apply for {opportunity.type} | VisionBird</title>
        <meta name="description" content={`Apply for ${opportunity.type} at VisionBird Technologies. Submit your application and join our team.`} />
      </Helmet>

      <Navbar />

      <div className="apply- padding-container">
        <div className="page-wrapper">
          <div className="apply-now">
            <div className="apply-from">
              {genderError ? (
                <div
                  className="gender-error-message"
                  style={{
                    height: "400px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    padding: "30px 20px",
                    backgroundColor: "white",
                    borderRadius: "12px",
                    marginTop: "60px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    gap: "20px",
                  }}
                >
                  <img
                    src="/assets/Images/office-pana.svg"
                    alt="Office Illustration"
                    style={{ maxHeight: "240px", width: "100%", objectFit: "contain" }}
                  />
                  <p
                    style={{
                      fontSize: "18px",
                      lineHeight: "1.5",
                      fontWeight: "500",
                      maxWidth: "600px",
                      color: "black",
                    }}
                  >
                    This opportunity is currently only open for {opportunity.gender}{" "}
                    candidates. We are working to make it available for both male
                    and female soon. Thank you for your interest!
                  </p>
                </div>
              ) : (
                <div className="form">
                  <h1>Apply Now</h1>

                  <p className="upload-resume" style={{ fontWeight: "bold" }}>
                    Upload or Link Your Resume
                  </p>

                  <div className="resume-option" style={{ marginBottom: "10px" }}>
                    <label>
                      <input
                        type="radio"
                        name="resumeOption"
                        value="file"
                        checked={formData.resumeOption === "file"}
                        onChange={handleChange}
                      />
                      Upload File
                    </label>
                    <label style={{ marginLeft: "15px" }}>
                      <input
                        type="radio"
                        name="resumeOption"
                        value="link"
                        checked={formData.resumeOption === "link"}
                        onChange={handleChange}
                      />
                      Paste Link
                    </label>
                  </div>

                  {formData.resumeOption === "file" ? (
                    <div className="resume" style={{ width: "100%" }}>
                      <label
                        htmlFor="resumeFile"
                        style={{
                          fontSize: "20px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "fit-content",
                          padding: "8px 20px",
                          borderRadius: "12px",
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          cursor: "pointer",
                          position: "relative",
                          transition: "border-color 0.2s",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "15px",
                            color: "#666",
                            fontWeight: "500",
                            pointerEvents: "none",
                          }}
                        >
                          {fileName ? "Upload another resume" : "Upload your resume"}
                        </div>
                        <input
                          key={fileName}
                          type="file"
                          id="resumeFile"
                          name="resumeFile"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          required
                          style={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            cursor: "pointer",
                            width: "100%",
                            height: "100%",
                          }}
                        />
                      </label>

                      {fileName && (
                        <div
                          style={{
                            marginTop: "10px",
                            padding: "8px 12px",
                            backgroundColor: "#eef2ff",
                            color: "#374151",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: "500",
                            display: "inline-block",
                            border: "1px solid #d1d5db",
                          }}
                        >
                          📄 {fileName}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="resume-link">
                      <input
                        type="url"
                        id="resumeLink"
                        name="resumeLink"
                        placeholder="Paste your resume link here"
                        style={{ width: "100%", padding: "12px", border: "1px solid #ccc", borderRadius: "8px" }}
                        value={formData.resumeLink}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  )}

                  <form className="form-data" onSubmit={handleSubmit}>
                    <div className="form-name">
                      <label htmlFor="name">Name</label>
                      <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required />
                    </div>

                    <div className="form-gender">
                      <label htmlFor="candidateGender">Select Gender</label>
                      <select name="candidateGender" id="candidateGender" value={formData.candidateGender} onChange={handleGenderChange} required>
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    <div className="form-phone">
                      <label htmlFor="selectedField">Available Fields</label>
                      <select name="selectedField" id="selectedField" value={formData.selectedField} onChange={handleChange} required>
                        <option value="">Select Available Field</option>
                        {availableFields.map((field, index) => (
                          <option key={index} value={field}>{field}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-phone">
                      <label htmlFor="interviewDate">Select Interview Date</label>
                      <input type="date" name="interviewDate" id="interviewDate" value={formData.interviewDate} onChange={handleDateChange} min={firstDay} required />
                    </div>

                    {formData.interviewDate && filteredSlots.length > 0 && (
                      <div className="form-phone">
                        <label htmlFor="interviewSlot">Select Interview Slot</label>
                        <select name="interviewSlot" id="interviewSlot" value={formData.interviewSlot} onChange={handleChange} required>
                          <option value="">-- Select Time Slot --</option>
                          {filteredSlots.map((slot, i) => (
                            <option key={i} value={slot.time}>{slot.label}: {slot.time}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="form-cnic">
                      <label htmlFor="cnic">CNIC Number</label>
                      <input
                        type="text"
                        name="cnic"
                        id="cnic"
                        placeholder="Enter your correct 13 digits CNIC number"
                        value={formData.cnic}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          if (value.length <= 13) setFormData({ ...formData, cnic: value });
                        }}
                        maxLength="13"
                        required
                      />
                    </div>

                    <div className="form-phone">
                      <label htmlFor="phone">Phone</label>
                      <input type="text" name="phone" id="phone" value={formData.phone} onChange={handleChange} required />
                    </div>

                    <div className="form-email">
                      <label htmlFor="email">Email</label>
                      <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} required />
                    </div>

                    <div className="form-email">
                      <label htmlFor="location">Location</label>
                      <input type="text" name="location" id="location" value={formData.location} onChange={handleChange} required />
                    </div>

                    <button type="submit" disabled={submitting || genderError}>
                      {submitting ? "Submitting..." : "Apply"}
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Right Side - Opportunity Details Card */}
            <div className="job-detail">
              <div className="job-top">
                <div className="top-detail">
                  <div className="job-date">
                    <p>{opportunity.date}</p>
                  </div>
                </div>
                <div className="job-tital">
                  <p className="tital">{opportunity.type}</p>
                </div>
              </div>
              <div className="job-buttom">
                <div className="buttom-down">
                  <h1>Description</h1>
                  <div style={{ fontFamily: "'poppins-regular', sans-serif" }} dangerouslySetInnerHTML={{ __html: opportunity.description || "No description provided." }} />
                  <h1>Gender Validity</h1>
                  <p>{opportunity.gender}</p>
                  <h1>Location</h1>
                  <p className="office-location">{opportunity.location || "Gujrat, Pakistan"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* ── CNIC 30-Day Restriction Modal ─────────────────────────────── */}
      {cnicBlockedModal.visible && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              padding: "44px 36px",
              maxWidth: "480px",
              width: "100%",
              textAlign: "center",
              boxShadow: "0 32px 64px rgba(0,0,0,0.2)",
              animation: "fadeInScale 0.25s ease",
            }}
          >
            {/* Icon */}
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #fff8f0 0%, #ffecd2 100%)",
                border: "2px solid #ffd199",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
                fontSize: "30px",
              }}
            >
              🕐
            </div>

            <h2
              style={{
                margin: "0 0 12px",
                color: "#1a202c",
                fontSize: "22px",
                fontWeight: "700",
                lineHeight: "1.3",
              }}
            >
              Application Under Review Period
            </h2>

            <p
              style={{
                margin: "0 0 8px",
                color: "#4a5568",
                fontSize: "15px",
                lineHeight: "1.7",
              }}
            >
              You have recently submitted an application to{" "}
              <strong style={{ color: "#ff8000" }}>VisionBird Technologies</strong>.
              We kindly ask you to wait at least{" "}
              <strong>30 days</strong> before submitting a new application.
            </p>

            <p
              style={{
                margin: "0 0 28px",
                color: "#718096",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              We truly appreciate your continued interest and encourage you to keep developing your skills in the meantime.
            </p>

            <button
              onClick={() => setCnicBlockedModal({ visible: false, daysLeft: 0 })}
              style={{
                background: "linear-gradient(135deg, #FF8C00 0%, #E67E00 100%)",
                color: "white",
                border: "none",
                padding: "14px 40px",
                borderRadius: "50px",
                fontSize: "15px",
                fontWeight: "700",
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(255,140,0,0.35)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(255,140,0,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(255,140,0,0.35)";
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ApplyReservation;